// Centralised route definitions - single source of truth for all client-side route paths

import type { IAppThunkAction } from '../store';
import { push, replace } from 'connected-react-router';
import type { CallHistoryMethodAction } from 'connected-react-router';
import { matchPath } from 'react-router';
import { isKnownLocale, replaceLanguageInPath } from './lang';

export enum Routes {
	BookNow = 'book-now',
	GiftVoucher = 'gift-voucher',
	PrivateEvent = 'private-event',
	Addons = 'addons',
	Checkout = 'checkout',
	CheckoutPtv = 'checkout-ptv',
	Complete = 'complete',
	Processing = 'processing',
	Booking = 'booking',
	SplitPayment = 'split-payment',
	SplitAllocate = 'split-allocate',
	SplitJoin = 'split-join',
	BalancePayment = 'balance-payment',
	RegionalGiftVoucher = 'regional-gift-voucher',
	GlobalGiftVoucher = 'global-gift-voucher',
}

// Route patterns for React Router <Route path> definitions
export const RoutePatterns = {
	BookNow: `/:language?/${Routes.BookNow}/:venue?/:accessCode?/:players?/:date?/:time?/:session?`,
	GiftVoucher: `/:language?/${Routes.GiftVoucher}/:venue?/:amount?`,
	PrivateEvent: `/:language?/${Routes.PrivateEvent}/:venue?/:accessCode?/:players?/:date?/:time?/:session?`,
	Addons: `/:language?/${Routes.Addons}/:venue?`,
	Checkout: `/:language?/${Routes.Checkout}/:venue?`,
	CheckoutPtv: `/:language?/${Routes.CheckoutPtv}/:venue?`,
	Complete: `/:language?/${Routes.Complete}/:venue?`,
	Booking: `/:language?/${Routes.Booking}/:id`,
	SplitPayment: `/:language?/${Routes.SplitPayment}/:shareToken`,
	SplitAllocate: `/:language?/${Routes.SplitAllocate}/:shareToken`,
	SplitJoin: `/:language?/${Routes.SplitJoin}/:shareToken`,
	BalancePayment: `/:language?/${Routes.BalancePayment}/:bookingRef`,
	Processing: `/:language?/${Routes.Processing}`,
	RegionalGiftVoucher: `/:language?/${Routes.RegionalGiftVoucher}`,
	RegionalGiftVoucherRedeem: `/:language?/${Routes.RegionalGiftVoucher}/:currency?/redeem-locations`,
	GlobalGiftVoucher: `/:language?/${Routes.GlobalGiftVoucher}`,
	GlobalGiftVoucherRedeem: `/:language?/${Routes.GlobalGiftVoucher}/:currency?/redeem-locations`,
	Root: `/:language?`,
} as const;

const routeValues = Object.values(Routes) as string[];

// Query string param carrying the preview access code. The legacy /:venue/:accessCode path
// segment is still accepted inbound (existing marketing links), but this is the canonical
// outbound form - it needs no route-pattern slot, so it survives the whole funnel.
export const AccessCodeParam = process.env.REACT_APP_ACCESSCODE_PARAM ?? 'ac';

// Routes the access code is allowed to appear on. Split/balance payment and booking-detail
// URLs get shared with third parties, so the token must not ride along with them.
const AccessCodeRoutes: ReadonlySet<Routes> = new Set([
	Routes.BookNow,
	Routes.PrivateEvent,
	Routes.GiftVoucher,
	Routes.Addons,
	Routes.Checkout,
	Routes.CheckoutPtv,
	Routes.Complete,
]);

// Normalises a raw access code from a URL. Returns undefined for anything unusable, so
// callers can treat "no code" and "junk code" identically.
// The /book-now/:venue?/:accessCode?/:players? segments are positional, so
// /book-now/melbourne/4 would otherwise be read as an access code of '4' - requiring at
// least one non-digit character rules that out.
export function sanitiseAccessCode(raw: string | null | undefined): string | undefined {
	const cleaned = raw?.trim() ?? '';
	if (!cleaned || !/[^0-9]/.test(cleaned)) return undefined;
	return cleaned.toLowerCase();
}

// Extracts the preview access code from a location, preferring the canonical ?ac= form and
// falling back to the legacy /:venue/:accessCode path segment used by older marketing links.
// Pure and component-free so it can run before the store exists - see store/localStorage.ts.
export function readAccessCodeFromLocation(location: { pathname: string; search: string }): string | undefined {
	const fromQuery = sanitiseAccessCode(new URLSearchParams(location.search).get(AccessCodeParam));
	if (fromQuery) return fromQuery;

	// the legacy segment only ever appears on these two routes
	const match =
		matchPath<{ accessCode?: string }>(location.pathname, { path: RoutePatterns.BookNow, exact: true }) ??
		matchPath<{ accessCode?: string }>(location.pathname, { path: RoutePatterns.PrivateEvent, exact: true });

	return sanitiseAccessCode(match?.params.accessCode);
}

/** Type guard: checks if a URL path segment matches a known route name (case-insensitive) */
export function isKnownRoute(segment: string): segment is Routes {
	if (!segment) return false;
	const lower = segment.toLowerCase();
	return routeValues.some((route) => route === lower);
}

/** Extracts the route segment from a pathname and returns the matching Routes enum value.
 * Handles both /{locale}/{route}/... and /{route}/... (no locale prefix).
 * Returns undefined if no segment matches a known route. */
export function getCurrentRoute(pathname: string): Routes | undefined {
	const segments = pathname.split('/').filter((s) => s !== '');
	if (segments.length === 0) return undefined;

	// If the first segment is a locale, the route is the next segment
	const routeSegment = isKnownLocale(segments[0]) ? segments[1] : segments[0];
	if (!routeSegment) return undefined;

	return isKnownRoute(routeSegment) ? routeSegment : undefined;
}

// Builds a navigation path: /{locale}/{route}/{venue}{queryString}
export function buildRoutePath(
	locale: string,
	route: string,
	venue?: string | null,
	queryString?: string
): string {
	let path = `/${locale}/${route}`;
	if (venue) path += `/${venue}`;
	if (queryString) path += queryString;
	return path;
}

// Ensures a URL pathname has the correct locale prefix. Returns the corrected URL string.
// If the first segment is a known locale, replaces it. Otherwise, prepends the locale.
export function ensureLocalePath(locale: string, pathname: string, search: string): string {
	const firstSegment = pathname.split('/').filter((s) => s !== '')[0] ?? '';

	if (isKnownLocale(firstSegment)) {
		// First segment is an existing locale -- replace it with the new one
		return replaceLanguageInPath(pathname, locale) + search;
	}

	// No locale prefix present -- prepend it
	return `/${locale}${pathname}${search}`;
}

// Thunk that reads locale from Redux state and navigates to the given route
export function navigateTo(
	route: Routes,
	venue?: string | null,
	queryString?: string,
	options?: { locale?: string; method?: 'push' | 'replace'; accessCode?: string }
): IAppThunkAction<CallHistoryMethodAction> {
	return (dispatch, getState) => {
		const state = getState();
		const locale = options?.locale ?? state.intl.selectedLanguage.momentLocale;

		// Carry the access code forward automatically. Doing it here rather than at the
		// ~30 navigateTo call sites means none of them can forget it (only one ever
		// remembered), and the next one written gets it for free.
		let query = queryString || '';
		const accessCode = state.sites?.accessCode;
		if (accessCode && AccessCodeRoutes.has(route)) {
			const params = new URLSearchParams(query);
			// set() rather than append() - several callers forward window.location.search,
			// which by now already contains the param
			params.set(AccessCodeParam, accessCode);
			query = `?${params.toString()}`;
		}

		const accessCodeSegment = options?.accessCode ? `/${options.accessCode}` : '';
		const fullQueryString = `${accessCodeSegment}${query}` || undefined;
		const path = buildRoutePath(locale, route, venue, fullQueryString);
		const navigate = options?.method === 'replace' ? replace : push;
		dispatch(navigate(path));
	};
}

// Thunk that ensures the current URL has the correct locale prefix and navigates.
// Idempotent: handles both "locale exists and needs swapping" and "locale is missing".
// If no locale arg, reads from Redux state.
export function navigateToLocale(
	newLocale?: string,
	options?: { method?: 'push' | 'replace' }
): IAppThunkAction<CallHistoryMethodAction> {
	return (dispatch, getState) => {
		const locale = newLocale ?? getState().intl.selectedLanguage.momentLocale;
		const newPath = ensureLocalePath(locale, window.location.pathname, window.location.search);
		const navigate = options?.method === 'replace' ? replace : push;
		dispatch(navigate(newPath));
	};
}

// Thunk that navigates to the root landing page: /{locale}/{queryString}
export function navigateToRoot(
	queryString?: string,
	options?: { method?: 'push' | 'replace' }
): IAppThunkAction<CallHistoryMethodAction> {
	return (dispatch, getState) => {
		const locale = getState().intl.selectedLanguage.momentLocale;
		let path = `/${locale}/`;
		if (queryString) path = `/${locale}${queryString}`;
		const navigate = options?.method === 'replace' ? replace : push;
		dispatch(navigate(path));
	};
}
